import React from 'react'
import { X, Crown, Loader2, AlertTriangle } from 'lucide-react'
import { useSetOfficialScenario, useListScenarios, type ProaScenario } from '../hooks/useProaData'
import { useProaStore } from '@/stores/proa-store'

interface SetOfficialModalProps {
  isOpen: boolean
  onClose: () => void
  scenario: ProaScenario
}

/**
 * Modal for setting a scenario as the official PROA for its year
 * Shows warning if another scenario is already official
 */
export function SetOfficialModal({ isOpen, onClose, scenario }: SetOfficialModalProps) {
  const { setSelectedScenario } = useProaStore()
  const { data: scenarios } = useListScenarios(scenario.year)
  const setOfficialMutation = useSetOfficialScenario()

  // Find current official scenario (if any)
  const currentOfficial = scenarios?.find(s => s.isOfficial && s.scenarioId !== scenario.scenarioId)

  const handleConfirm = async () => {
    try {
      const updatedScenario = await setOfficialMutation.mutateAsync({
        scenarioId: scenario.scenarioId,
      })
      // Update selected scenario with new data
      setSelectedScenario(updatedScenario)
      onClose()
    } catch (error) {
      console.error('Failed to set official scenario:', error)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#1E2024] rounded-xl border border-[#2A2D34] w-full max-w-md mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2A2D34]">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-[#D2FA52]" />
            <h2 className="text-lg font-semibold text-white">Definir como Oficial</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-[#2A2D34] transition-colors"
          >
            <X className="h-5 w-5 text-[#838998]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="bg-[#24262B] rounded-lg p-4 border border-[#2A2D34]">
            <p className="text-sm text-[#838998] mb-1">Cenario selecionado:</p>
            <p className="text-white font-medium">{scenario.name}</p>
            <p className="text-xs text-[#838998] mt-1">Ano: {scenario.year}</p>
          </div>

          {currentOfficial && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-orange-400 font-medium text-sm">Atencao</p>
                  <p className="text-sm text-[#838998] mt-1">
                    O cenario <strong className="text-white">{currentOfficial.name}</strong> ja esta
                    definido como oficial para {scenario.year}. Ao confirmar, ele deixara de ser oficial.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-[#D2FA52]/10 border border-[#D2FA52]/30 rounded-lg p-4">
            <p className="text-sm text-[#838998]">
              O cenario oficial e usado como referencia nos dashboards financeiros para comparar metas planejadas vs resultados realizados.
            </p>
          </div>

          {/* Error */}
          {setOfficialMutation.error && (
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-400">
                Erro ao definir cenario oficial: {setOfficialMutation.error.message}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-[#2A2D34]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#838998] hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={setOfficialMutation.isPending}
            className="px-4 py-2 text-sm font-medium bg-[#D2FA52] text-[#0D0E10] rounded-lg hover:bg-[#c2ea42] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {setOfficialMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crown className="h-4 w-4" />
            )}
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}

export default SetOfficialModal
